import { clamp } from '../core/math';
import { isLocked, type LayerBulk } from '../core/params/bulk';
import { randomizeParam, type RandomizeStrength } from '../core/params/randomize';
import { TEXT_LAYER_PARAMS } from '../core/params/textLayer';
import { defaultValues, type Param, type ParamSchema, type ParamValue, type ParamValues } from '../core/params/types';
import { CURATED_PALETTES, clonePalette, coercePalette, type PaletteSpec } from '../core/palette';
import { randomSeed, type Rng } from '../core/rng';
import { buildTextSchema } from '../core/text/textParams';
import { getTechnique, listTechniques } from '../techniques/registry';
import { TEXT_TECHNIQUE_ID, type Layer } from '../store/layer';
import type { StudioDocument } from '../store/studioStore';

/**
 * Breeding compositions.
 *
 * The premise of the evolution mode is that judging a picture is easy and describing
 * why you like it is hard. So the user never touches a slider here: they point at the
 * variants that appeal to them, and the next generation is built from those.
 *
 * Three operators do the work, and each is chosen for what it does to a *design* rather
 * than for genetic-algorithm orthodoxy:
 *
 *  - **Block crossover** takes a whole layer from one parent. Parameters within a layer
 *    are not independent — a stroke width that suits one particle count looks wrong at
 *    another — so recombining them freely destroys more than it discovers. Half the
 *    time a layer is inherited intact.
 *  - **Blending crossover** on continuous parameters takes a point *between* two
 *    parents rather than one or the other. With two results you like, the interesting
 *    region is usually between them, and picking endpoints can never go there.
 *  - **Sparse mutation** changes a few parameters rather than nudging all of them. A
 *    child that differs from its parent in three ways is legible as "the same idea,
 *    varied"; one that differs slightly in forty ways just looks like noise.
 *
 * Locks and exploration ranges apply throughout, exactly as in the studio: a parameter
 * you have pinned stays pinned across every generation.
 */
export interface EvolveConfig {
  populationSize: number;
  /** 0–1. Drives both how many parameters change and by how much. */
  mutation: number;
  mutateSeeds: boolean;
  mutateText: boolean;
  mutatePalettes: boolean;
  /** Occasionally swap a technique for another from the same category. */
  mutateTechniques: boolean;
  /** Carry the chosen parents into the next generation unchanged. */
  elitism: boolean;
}

export const DEFAULT_EVOLVE_CONFIG: EvolveConfig = {
  populationSize: 12,
  mutation: 0.35,
  mutateSeeds: true,
  mutateText: false,
  mutatePalettes: true,
  mutateTechniques: false,
  elitism: true,
};

function strengthFor(mutation: number): RandomizeStrength {
  return mutation < 0.35 ? 'gentle' : mutation < 0.7 ? 'medium' : 'wild';
}

/** Probability that any one parameter is touched. */
function mutationRate(mutation: number): number {
  return 0.08 + 0.55 * mutation;
}

function mutateValues(
  schema: ParamSchema,
  values: ParamValues,
  bulk: LayerBulk | undefined,
  rng: Rng,
  config: EvolveConfig,
): ParamValues {
  const rate = mutationRate(config.mutation);
  const strength = strengthFor(config.mutation);
  const out: ParamValues = { ...values };

  for (const param of schema) {
    if (isLocked(bulk, param.key)) continue;

    if (param.kind === 'palette') {
      if (config.mutatePalettes && rng.chance(rate * 0.6)) {
        out[param.key] = mutatePalette(values[param.key], rng);
      }
      continue;
    }
    if (!rng.chance(rate)) continue;
    out[param.key] = randomizeParam(param, values[param.key], bulk?.[param.key], rng, strength);
  }
  return out;
}

/**
 * Palettes mutate by switching to another curated set, or by nudging a generative one.
 *
 * Left to the generic randomiser a palette would never change at all — it has no
 * numeric range to draw from — and colour is far too large a part of how a composition
 * reads for it to be the one thing evolution cannot touch.
 */
function mutatePalette(current: ParamValue, rng: Rng): PaletteSpec {
  const spec = coercePalette(current);
  if (spec.source === 'generative' && rng.chance(0.5)) {
    return { ...clonePalette(spec), harmony: spec.harmony, count: clamp(spec.count + rng.int(-1, 1), 2, 9) };
  }
  const others = CURATED_PALETTES.filter((p) => p.id !== spec.paletteId);
  if (!others.length) return spec;
  return { ...clonePalette(spec), source: 'curated', paletteId: rng.pick(others).id, reverse: rng.chance(0.25) };
}

/**
 * Combines the same parameter from several parents.
 *
 * Continuous values blend; everything else is inherited whole, because there is no
 * meaningful midpoint between "Kontur" and "Füllung".
 */
function crossoverValues(schema: ParamSchema, parents: ParamValues[], rng: Rng): ParamValues {
  const out: ParamValues = {};
  for (const param of schema) {
    const available = parents.filter((p) => p[param.key] !== undefined);
    if (!available.length) {
      out[param.key] = param.default as ParamValue;
      continue;
    }
    if (available.length > 1 && (param.kind === 'float' || param.kind === 'int') && rng.chance(0.55)) {
      out[param.key] = blend(param, available, rng);
      continue;
    }
    out[param.key] = available[rng.int(0, available.length - 1)][param.key];
  }
  return out;
}

/** A point between two parents, allowed to overshoot slightly so the range can widen. */
function blend(param: Param, parents: ParamValues[], rng: Rng): ParamValue {
  if (param.kind !== 'float' && param.kind !== 'int') return param.default as ParamValue;
  const a = parents[rng.int(0, parents.length - 1)][param.key];
  const b = parents[rng.int(0, parents.length - 1)][param.key];
  if (typeof a !== 'number' || typeof b !== 'number') return typeof a === 'number' ? a : param.default;

  // t outside [0, 1] lets children fall just beyond both parents; without it a
  // population converges on the interval its first generation happened to span.
  const t = -0.15 + rng.next() * 1.3;
  const raw = a + (b - a) * t;
  const snapped = Math.round(raw / param.step) * param.step;
  const clamped = clamp(snapped, param.min, param.max);
  return param.kind === 'int' ? Math.round(clamped) : Number(clamped.toFixed(6));
}

function pickReplacementTechnique(currentId: string, rng: Rng): string {
  const current = getTechnique(currentId);
  if (!current) return currentId;
  // Same category: a swap should be a related idea, not a different artwork.
  const siblings = listTechniques().filter((t) => t.category === current.category && t.id !== currentId);
  return siblings.length ? rng.pick(siblings).id : currentId;
}

/**
 * Builds one child from the chosen parents.
 *
 * Layers are matched by position, which holds because every genome descends from the
 * same starting composition and the stack is never reordered here.
 */
function breedOne(parents: StudioDocument[], rng: Rng, config: EvolveConfig): StudioDocument {
  const template = parents[rng.int(0, parents.length - 1)];
  const child: StudioDocument = structuredClone(template);

  child.seed = config.mutateSeeds && !child.seedLocked ? randomSeed() : template.seed;

  if (config.mutateText) {
    const textSchema = buildTextSchema(null);
    const textParents = parents.map((p) => p.textValues);
    child.textValues = mutateValues(
      textSchema,
      crossoverValues(textSchema, textParents, rng),
      child.textBulk,
      rng,
      config,
    );
  }

  child.layers = child.layers.map((layer, index) => {
    const cohort = parents
      .map((p) => p.layers[index])
      .filter((l): l is Layer => Boolean(l) && l.techniqueId === layer.techniqueId);

    let next: Layer = { ...layer, values: { ...layer.values } };

    if (layer.techniqueId !== TEXT_TECHNIQUE_ID && config.mutateTechniques && rng.chance(0.1 * config.mutation)) {
      const swapped = pickReplacementTechnique(layer.techniqueId, rng);
      const technique = getTechnique(swapped);
      if (technique && swapped !== layer.techniqueId) {
        return {
          ...layer,
          techniqueId: swapped,
          name: technique.name,
          values: defaultValues(technique.params),
          bulk: {},
          seed: randomSeed(),
        };
      }
    }

    const schema = schemaFor(next);
    // Half the time the layer is inherited whole; otherwise its parameters recombine.
    if (cohort.length > 1 && !rng.chance(0.5)) {
      next = { ...next, values: crossoverValues(schema, cohort.map((l) => l.values), rng) };
    } else if (cohort.length) {
      const donor = cohort[rng.int(0, cohort.length - 1)];
      next = { ...next, values: { ...donor.values }, seed: donor.seed };
    }

    next.values = mutateValues(schema, next.values, next.bulk, rng, config);
    if (config.mutateSeeds && !next.seedLocked) next.seed = randomSeed();
    return next;
  });

  return child;
}

function schemaFor(layer: Layer): ParamSchema {
  if (layer.techniqueId === TEXT_TECHNIQUE_ID) return TEXT_LAYER_PARAMS;
  return getTechnique(layer.techniqueId)?.params ?? [];
}

/**
 * The next generation.
 *
 * With no parents chosen this is simply a spread of mutations around the base — which
 * is also how the first generation is produced, so the same code covers "start" and
 * "carry on".
 */
export function breedPopulation(
  parents: readonly StudioDocument[],
  base: StudioDocument,
  config: EvolveConfig,
  rng: Rng,
): StudioDocument[] {
  const pool = parents.length ? [...parents] : [base];
  const out: StudioDocument[] = [];

  // Elites are carried over untouched so a result you liked is never lost to chance.
  if (config.elitism && parents.length) {
    const keep = Math.min(parents.length, Math.max(1, Math.floor(config.populationSize / 4)));
    for (let i = 0; i < keep; i++) out.push(structuredClone(parents[i]));
  }

  while (out.length < config.populationSize) {
    // Two parents when there are two to have, so crossover has something to combine.
    const mates = pool.length > 1 ? rng.shuffle([...pool]).slice(0, 2) : pool;
    out.push(breedOne(mates, rng, config));
  }
  return out;
}
